import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import axios from 'axios'
import {
  MeasuresTableWrapper,
  MeasuresTable,
  StickyTh,
  StickyTd,
  Th,
  Tr,
  MeasuresInputTd,
  MeasuresCellInput,
} from '../../../ProductComposition/ApprovationModal/styles'
import {
  StyledCard,
  CardTitle,
  CollapsePanelHeader,
} from '../style'

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const FASHION_API_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`

interface Grade {
  id: number
  name: string
}

interface MeasureType {
  id: number
  name: string
}

interface CellData {
  value: string
  tolerance: string
  graduation: string
}

type MeasuresState = Record<number, Record<number, CellData>>

export interface MeasuresHandle {
  save: (productId: number) => Promise<void>
}

interface Props {
  productId: number
  groupId: number
  selectedGrades: Grade[]
}

const emptyCell = (): CellData => ({ value: '', tolerance: '', graduation: '' })

const Measures = forwardRef<MeasuresHandle, Props>(
  ({ productId, groupId, selectedGrades }, ref) => {
    const [isOpen, setIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [measureTypes, setMeasureTypes] = useState<MeasureType[]>([])
    const [cellData, setCellData] = useState<MeasuresState>({})

    const selectedGradesRef = useRef(selectedGrades)
    useEffect(() => {
      selectedGradesRef.current = selectedGrades
    })

    const measureTypesRef = useRef(measureTypes)
    useEffect(() => {
      measureTypesRef.current = measureTypes
    }, [measureTypes])

    const cellDataRef = useRef(cellData)
    useEffect(() => {
      cellDataRef.current = cellData
    }, [cellData])

    const fetchData = useCallback(async () => {
      if (!groupId) return
      setLoading(true)
      try {
        const token = localStorage.getItem('token')
        const headers = { Authorization: `Bearer ${token}` }

        const validProductId = productId && !isNaN(productId) ? productId : null

        const [typesRes, measuresRes] = await Promise.all([
          axios.get(`${FASHION_API_URL}/measure-types`, {
            headers,
            params: { groupId },
          }),
          validProductId
            ? axios.get(`${FASHION_API_URL}/${validProductId}/measures`, { headers })
            : Promise.resolve({ data: [] }),
        ])

        const types: MeasureType[] = Array.isArray(typesRes.data) ? typesRes.data : []
        setMeasureTypes(types)

        const grades = selectedGradesRef.current
        const initial: MeasuresState = {}
        types.forEach((t) => {
          initial[t.id] = {}
          grades.forEach((g) => {
            initial[t.id][g.id] = emptyCell()
          })
        })

        const existingMeasures: any[] = Array.isArray(measuresRes.data) ? measuresRes.data : []
        existingMeasures.forEach((m) => {
          if (!initial[m.measureTypeId]) initial[m.measureTypeId] = {}
          initial[m.measureTypeId][m.gradeId] = {
            value: m.value != null ? String(m.value) : '',
            tolerance: m.tolerance != null ? String(m.tolerance) : '',
            graduation: m.graduation != null ? String(m.graduation) : '',
          }
        })

        setCellData(initial)
      } catch (err) {
        console.error('Erro ao carregar medidas:', err)
      } finally {
        setLoading(false)
      }
    }, [groupId, productId])

    useEffect(() => {
      if (isOpen) {
        fetchData()
      }
    }, [isOpen, fetchData])

    const handleChange = (
      measureTypeId: number,
      gradeId: number,
      field: keyof CellData,
      val: string,
    ) => {
      setCellData((prev) => ({
        ...prev,
        [measureTypeId]: {
          ...prev[measureTypeId],
          [gradeId]: {
            ...(prev[measureTypeId]?.[gradeId] ?? emptyCell()),
            [field]: val,
          },
        },
      }))
    }

    useImperativeHandle(ref, () => ({
      save: async (targetProductId: number) => {
        const types = measureTypesRef.current
        const data = cellDataRef.current
        const grades = selectedGradesRef.current

        if (!types.length || !grades.length) return

        const token = localStorage.getItem('token')
        const headers = { Authorization: `Bearer ${token}` }

        const rows = types.map((t) => ({
          measureTypeId: t.id,
          values: grades.map((g) => {
            const cell = data[t.id]?.[g.id] ?? emptyCell()
            return {
              gradeId: g.id,
              value: cell.value !== '' ? Number(cell.value.replace(',', '.')) : undefined,
              tolerance: cell.tolerance !== '' ? Number(cell.tolerance.replace(',', '.')) : undefined,
              graduation: cell.graduation !== '' ? Number(cell.graduation.replace(',', '.')) : undefined,
            }
          }),
        }))

        await axios.put(
          `${FASHION_API_URL}/${targetProductId}/measures`,
          { rows },
          { headers },
        )
      },
    }))

    const hasData = !loading && measureTypes.length > 0 && selectedGrades.length > 0

    return (
      <StyledCard>
        <CollapsePanelHeader
          $open={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <CardTitle>Medidas</CardTitle>
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </CollapsePanelHeader>

        {isOpen && (
          <>
            {loading && (
              <p style={{ fontSize: 13, color: '#6b7280' }}>Carregando medidas...</p>
            )}

            {!loading && !groupId && (
              <p style={{ fontSize: 13, color: '#9ca3af' }}>
                Selecione um grupo para visualizar os tipos de medida.
              </p>
            )}

            {!loading && groupId && measureTypes.length === 0 && (
              <p style={{ fontSize: 13, color: '#9ca3af' }}>
                Nenhum tipo de medida cadastrado para este grupo.
              </p>
            )}

            {!loading && groupId && selectedGrades.length === 0 && measureTypes.length > 0 && (
              <p style={{ fontSize: 13, color: '#9ca3af' }}>
                Selecione ao menos uma graduação para preencher as medidas.
              </p>
            )}

            {hasData && (
              <MeasuresTableWrapper>
                <MeasuresTable>
                  <thead>
                    <tr>
                      <StickyTh>Tipo de Medida</StickyTh>
                      <Th>Tolerância</Th>
                      <Th>Gradação</Th>
                      {selectedGrades.map((g) => (
                        <Th key={g.id}>{g.name}</Th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {measureTypes.map((mt) => (
                      <Tr key={mt.id}>
                        <StickyTd>{mt.name}</StickyTd>
                        <MeasuresInputTd>
                          <MeasuresCellInput
                            type="number"
                            step="0.01"
                            placeholder="0"
                            value={cellData[mt.id]?.[selectedGrades[0]?.id]?.tolerance ?? ''}
                            onChange={(e) =>
                              selectedGrades.forEach((g) =>
                                handleChange(mt.id, g.id, 'tolerance', e.target.value),
                              )
                            }
                          />
                        </MeasuresInputTd>
                        <MeasuresInputTd>
                          <MeasuresCellInput
                            type="number"
                            step="0.01"
                            placeholder="0"
                            value={cellData[mt.id]?.[selectedGrades[0]?.id]?.graduation ?? ''}
                            onChange={(e) =>
                              selectedGrades.forEach((g) =>
                                handleChange(mt.id, g.id, 'graduation', e.target.value),
                              )
                            }
                          />
                        </MeasuresInputTd>
                        {selectedGrades.map((g) => (
                          <MeasuresInputTd key={g.id}>
                            <MeasuresCellInput
                              type="number"
                              step="0.01"
                              placeholder="0"
                              value={cellData[mt.id]?.[g.id]?.value ?? ''}
                              onChange={(e) =>
                                handleChange(mt.id, g.id, 'value', e.target.value)
                              }
                            />
                          </MeasuresInputTd>
                        ))}
                      </Tr>
                    ))}
                  </tbody>
                </MeasuresTable>
              </MeasuresTableWrapper>
            )}
          </>
        )}
      </StyledCard>
    )
  },
)

Measures.displayName = 'Measures'

export default Measures
